import { ITranslationMap } from '@principle-theorem/practice-migrations/interfaces';
import {
  IManualTransactionType,
  TransactionProvider,
  TransactionType,
} from '@principle-theorem/principle-core/interfaces';
import { INamedDocument, WithPrimaryKey } from '@principle-theorem/shared';
import { ExactV2TransactionType } from '../../../lib/exact-v2-transaction-type';
import { type IExactV2Transaction } from '../../../source/entities/patient-transactions-v2';
import { resolvePaymentMethodTransactionType } from '../../mappings/payment-method';
import { resolvePayorTransactionType } from '../../mappings/payor-label';
import { ExactTransactionLedger } from './exact-transaction-ledger';

export interface IExactTransactionMetadata {
  type: TransactionType;
  provider: TransactionProvider;
  manualType?: INamedDocument<IManualTransactionType>;
}

export interface IExactPayor {
  name: string;
  manualType?: INamedDocument<IManualTransactionType>;
}

const UNMAPPED_PAYOR_NAME = 'Third-party payment';

/**
 * Exact spreads who paid and how across three unrelated columns: transaction
 * type, an operator-mapped payor id, and a free-text reference.
 */
export class ExactTransactionLabels {
  constructor(
    private _payorLabels: WithPrimaryKey<
      ITranslationMap<IManualTransactionType, string>
    >[],
    private _paymentMethods: WithPrimaryKey<
      ITranslationMap<IManualTransactionType, string>
    >[]
  ) {}

  private static _isWriteDown(paymentRow: IExactV2Transaction): boolean {
    if (ExactTransactionLedger.isInvoiceSideAdjustment(paymentRow)) {
      return true;
    }
    return [
      ExactV2TransactionType.WriteOff,
      ExactV2TransactionType.Discount,
      ExactV2TransactionType.ExternalCredit,
    ].includes(paymentRow.transactionType);
  }

  /** Payor wins over payment method: who supplied the money beats how it arrived. */
  resolveMeta(paymentRow: IExactV2Transaction): IExactTransactionMetadata {
    if (ExactTransactionLabels._isWriteDown(paymentRow)) {
      return {
        type: TransactionType.Incoming,
        provider: TransactionProvider.Discount,
      };
    }

    if (ExactTransactionLedger.isReceiptSideAdjustment(paymentRow)) {
      return {
        type: TransactionType.Incoming,
        provider: TransactionProvider.Manual,
      };
    }

    const payorType = resolvePayorTransactionType(
      this._payorLabels,
      paymentRow.payorId
    );
    if (payorType) {
      return {
        type: TransactionType.Incoming,
        provider: payorType.provider,
        manualType: payorType.manualType,
      };
    }

    const methodType = resolvePaymentMethodTransactionType(
      this._paymentMethods,
      paymentRow.reference
    );

    return {
      type: TransactionType.Incoming,
      provider: TransactionProvider.Manual,
      manualType: methodType,
    };
  }

  describe(paymentRow: IExactV2Transaction): string {
    switch (paymentRow.transactionType) {
      case ExactV2TransactionType.WriteOff:
        return 'Account write-off';
      case ExactV2TransactionType.Discount:
        return paymentRow.comment ?? 'Discount';
      case ExactV2TransactionType.ExternalCredit:
        return paymentRow.comment ?? 'External credit';
      default:
        return (
          this.payor(paymentRow.payorId).manualType?.name ??
          paymentRow.reference ??
          ''
        );
    }
  }

  payor(payorId?: string): IExactPayor {
    const manualType = resolvePayorTransactionType(this._payorLabels, payorId)
      ?.manualType;
    return { name: manualType?.name ?? UNMAPPED_PAYOR_NAME, manualType };
  }
}
